import { Map } from 'immutable';
import { sortBy } from 'lodash';
import { createSelector, createStructuredSelector, Selector } from 'reselect';

import { DataViewTypes, FeatureFlag, ReportTypes } from '@mode/shared/contract-common';

import { fromChartSelectors } from '@mode/charts/domain';
import { fileExportSelectors } from '@mode/file-exports/domain';
import { LegacyReportFilterControl, ReportFilterControlHelpers } from '@mode/filters/util';
import { AppState } from '@mode/legacy/types-app-state';
import { fromReportFilterSelectors, fromReportViewerSelectors } from '@mode/report-viewer/domain';
import { virtualScrollSelectors } from '@mode/reports/domain';
import { EmbedReportState } from '@mode/reports/sandbox/contract';
import { getInitialFlagValue, getSecretToken, recordToJSObject } from '@mode/shared/util-js';
import { fromDatasetResultSelectors } from '@mode/switch-views/domain';
import { NotebookHelpers } from '../../lib/notebooks';
import { ChartResFunctions } from '../../lib/resources/chart.funcs';
import { TableResFunctions } from '../../lib/resources/table.funcs';
import {
  reportLevelColorMaps,
  selectedReportReportRunStateSelector,
  selectedReportSelector,
} from '../../reducers/selectors';
import { FeatureSelectors } from '../feature.selectors';
import * as FlamingoEmbedStateSelectors from './flamingo-embed-state.selectors';

/**
 * This is for building a serializable state for use in the iframe. Everything
 * returned by the embedState(Gf) selector will be serialized and synchronized
 * to the iframe. Then the embed-report.selectors can be used to pick out the
 * state from there.
 *
 * Since this file runs in the main app it can pull state from any module that
 * is necessary.
 */

export namespace BuildEmbedReportSelectors {
  export const account = createSelector(
    (state: AppState) => state.client.loggedIn,
    (state: AppState) => state.client.accountContext,
    (loggedIn, accountResource): EmbedReportState.AccountInfo => {
      return {
        loggedIn,
        whiteLabel: getSecretToken(window.document) != null, // hacky until this is pushed into the state
        embedded: document.body.getAttribute('ng-controller') === 'EmbedController', // testing frame context, `mode-runner` (and ng-controller="EmbedController") is main report page body, `mode-embedded` is the chart body tag
        isThoughtspotIntegrated: accountResource?.thoughtspot_integrated ?? false,
      };
    }
  );

  export const reportInfo = createSelector(
    (state: AppState) => selectedReportSelector(state).report?.token,
    (state: AppState) => selectedReportSelector(state).renderedReportLayout,
    (state: AppState) => selectedReportSelector(state).theme != null && selectedReportSelector(state).theme,
    (state: AppState) => selectedReportSelector(state).reportDisplayName,
    (state: AppState) => selectedReportSelector(state).report?.description,
    (state: AppState) => selectedReportSelector(state).report?.full_width,
    (state: AppState) => selectedReportSelector(state).report?.drill_anywhere_enabled,
    (token, layout, theme, name, description, fullWidth, drillAnywhereEnabled): EmbedReportState.ReportInfo => ({
      token,
      layout,
      name,
      description,
      fullWidth,
      theme: {
        href: theme.href,
        customSource: theme.customSource,
      },
      drillAnywhereEnabled,
    })
  );

  // GF version
  export const reportInfoGf = createSelector(
    fromReportViewerSelectors.getToken,
    fromReportViewerSelectors.getLayout,
    fromReportViewerSelectors.getTheme,
    fromReportViewerSelectors.getName,
    fromReportViewerSelectors.getDescription,
    fromReportViewerSelectors.getFullWidth,
    fromReportViewerSelectors.getDrillAnywhereEnabled,
    (token, layout, theme, name, description, fullWidth, drillAnywhereEnabled) => ({
      token,
      layout,
      name,
      description,
      fullWidth,
      theme: {
        href: theme.href,
        customSource: theme.customSource,
      },
      drillAnywhereEnabled,
    })
  );

  export const reportRunToken = createSelector(
    (state: AppState) =>
      selectedReportReportRunStateSelector(state) && selectedReportReportRunStateSelector(state).token,
    (token) => token
  );

  // TODO: for now we'll use this in the GF version
  export const reportRun = createSelector(
    reportRunToken,
    (state: AppState) =>
      selectedReportReportRunStateSelector(state) && selectedReportReportRunStateSelector(state).running,
    (token, running) => ({
      token,
      running,
    })
  );

  // TODO: come back to actually implement this when we need to handle polling
  export const tables = createSelector(
    (state: AppState) => state.client.selectedReport.tables,
    reportRunToken,
    (records, runToken) =>
      records.getRecords().reduce(
        (m, t) => {
          m[t.token] = {
            view: t.currentView,
            queryToken: t.queryToken,
            token: t.token,
            runLink: TableResFunctions.getVizReportRunLink(t.resource, runToken),
            vizLink: TableResFunctions.getVizLink(t.resource),
          };
          return m;
        },
        {} as { [token: string]: EmbedReportState.Table }
      )
  );

  // As of yet there is no GF version for notebooks as they depend on the legacy flow. Notebooks will be
  // migrated at some point after the initial GF release.
  export const notebookViz = createSelector(
    (state: AppState) => state.client.selectedReport.notebookViz.titles,
    (state: AppState) => state.client.selectedReport.notebookViz.tokens,
    (state: AppState) => state.client.selectedReport.notebookViz.resources,
    (state: AppState) => state.client.selectedReport.notebookViz.cellTokens,
    (state: AppState) => state.client.selectedReport.notebookViz.cellRunTokens,
    (state: AppState) => state.client.selectedReport.notebookViz.runState,
    (state: AppState) => state.client.notebooks.cellRuns.resultTokens,
    (state: AppState) => state.client.notebooks.cellRuns.missingResults,
    (state: AppState) => state.client.notebooks.cellRunResults,
    reportRunToken,
    (
      titles,
      vizTokens,
      vizResources,
      cellTokens,
      runTokens,
      runState,
      resultTokens,
      missingResults,
      cellRunResults,
      runToken
    ): EmbedReportState.NotebookViz => {
      const vizToResultTokens = cellTokens
        .map((cellToken) => runTokens.get(cellToken))
        .map((runToken) => resultTokens.get(runToken));
      const { contents, resultTypes, contentTypes, dates } = cellRunResults;

      return {
        titles: titles.toObject(),
        vizTokens: vizTokens.toArray(),
        cellTokens: cellTokens.toObject(),
        runState: runState,
        contents: vizToResultTokens
          .map((resTokens) => resTokens && resTokens.toArray().map((resToken) => contents.get(resToken)))
          .toObject(),
        resultTypes: vizToResultTokens
          .map((resTokens) => resTokens && resTokens.toArray().map((resToken) => resultTypes.get(resToken)))
          .toObject(),
        dates: vizToResultTokens
          .map((resTokens) => resTokens && resTokens.toArray().map((resToken) => dates.get(resToken)))
          .toObject(),
        contentTypes: vizToResultTokens
          .map((resTokens) => resTokens && resTokens.toArray().map((resToken) => contentTypes.get(resToken)))
          .toObject(),
        missingResults: Map(vizTokens.map((token) => [token, missingResults.has(runTokens[token])])).toObject(),
        vizLinks: vizResources.map((r) => r.links('report_pythonviz_web').href).toObject(),
        vizRunLinks: vizResources.map((r) => NotebookHelpers.cellVizLink(r, runToken)).toObject(),
      };
    }
  );

  export const charts = createStructuredSelector({
    chartTypes: createSelector(fromChartSelectors.selectChartTypes, (chartTypes) => chartTypes.toObject()),
    colorPaletteTokens: createSelector(fromChartSelectors.selectColorPaletteTokens, (colorPaletteTokens) =>
      colorPaletteTokens.toObject()
    ),
    encodings: createSelector(fromChartSelectors.selectEncodings, (encodings) => encodings.toObject()),
    formats: createSelector(
      (state: AppState) => fromChartSelectors.selectChartState(state).formats,
      (formats) => formats.toObject()
    ),
    fieldFormats: createSelector(
      (state: AppState) => fromChartSelectors.selectChartState(state).fieldFormats,
      (fieldFormats) => fieldFormats.toObject()
    ),
    fieldColors: createSelector(
      (state: AppState) => fromChartSelectors.selectChartState(state).fieldColors,
      (fieldColors) => fieldColors.toObject()
    ),
    facetSizes: createSelector(
      (state: AppState) => fromChartSelectors.selectChartState(state).facetSizes,
      (facetSizes) => facetSizes.toObject()
    ),
    isAutoUpdateDisabled: createSelector(fromChartSelectors.selectIsAutoUpdateDisabled, (isAutoUpdateDisabled) =>
      isAutoUpdateDisabled.toObject()
    ),
    histories: createSelector(fromChartSelectors.selectHistories, (histories) => histories.toObject()),
    queryTokens: createSelector(fromChartSelectors.selectQueryTokens, (queryTokens) => queryTokens.toObject()),
    switchViewTokens: createSelector(fromChartSelectors.selectSwitchViewTokens, (switchViewTokens) =>
      switchViewTokens.toObject()
    ),
    titles: createSelector(fromChartSelectors.selectTitles, (titles) => titles.toObject()),
    descriptions: createSelector(fromChartSelectors.selectDescriptions, (titles) => titles.toObject()),
    captions: createSelector(fromChartSelectors.selectCaptions, (captions) => captions.toObject()),
    tokens: createSelector(fromChartSelectors.selectTokens, (tokens) => tokens.toArray()),
    v1Views: createSelector(fromChartSelectors.selectV1Views, (v1Views) => v1Views.toObject()),
    versions: createSelector(fromChartSelectors.selectVersions, (versions) => versions.toObject()),
    runLinks: createSelector(fromChartSelectors.selectResources, reportRunToken, (resources, runToken) =>
      resources.map((r) => ChartResFunctions.getVizReportRunLink(r, runToken)).toObject()
    ),
    vizLinks: createSelector(fromChartSelectors.selectResources, (resources) =>
      resources.map((r) => ChartResFunctions.getVizLink(r)).toObject()
    ),
    createdTimestamps: createSelector(fromChartSelectors.selectCreatedTimestamps, (createdTimestamps) =>
      createdTimestamps.toArray()
    ),
  });

  // TODO: use DataView Selector for run data instead of selectedReport.queryRuns
  export const legacyEmbedDatasets = createSelector(
    (state: AppState) => state.transient.report.datasets,
    (state: AppState) => state.client.selectedReport.queryRuns,
    (models, runs) =>
      models.getRecords().reduce<{ [token: string]: EmbedReportState.EmbedDataset }>((s, m) => {
        // Don't return a dataset if it's running. Otherwise placeholders won't appear on reruns of a query.
        const run = runs.getRecord(m.id);
        s[m.id] =
          run != null && !['pending', 'running', 'enqueued'].includes(run.state)
            ? {
                token: m.id,
                query_token: m.query_token || m.token, // need to be safe due to loose typings on datasets
                content: m.content,
                columns: m.columns,
                content_length: m.content_length,
                count: m.count,
                bytes: m.bytes,
                csv: m.csv,
                oversized: m.oversized,
                reportQueryUrl: m.reportQueryUrl,
                reportDataUrl: m.reportDataUrl,
                state: m.state,
                queryName: m.queryName,
              }
            : null;
        return s;
      }, {})
  );

  // TODO: MAKE A GF VERSION - we won't have `queries`, but can derive from charts
  export const localQueryTokensSortedByCreationDateFilterdByAvailableDatasets = createSelector(
    (state: AppState) => state.transient.report.datasets,
    (state: AppState) => state.client.selectedReport.queries,
    (datasets, queries) => {
      if (getInitialFlagValue(FeatureFlag.CustomJsDatasetSupport)) return []; // Mark for delete
      const orderedQueries = sortBy(queries.getRecords(), 'resource.created_at');
      return orderedQueries.map((q) => q.token).filter((token) => datasets.hasRecord(token));
    }
  );

  export const localQueryTokensSortedByQueryCreationDate = createSelector(
    (state: AppState) => state.client.selectedReport.queries,
    (queries) => {
      const orderedQueries = sortBy(queries.getRecords(), 'resource.created_at');
      return orderedQueries.map((q) => q.token);
    }
  );

  export const queryRunStatus = createSelector(fromDatasetResultSelectors.getDatasetResults, (datasets) => {
    let queryRunStatuses = {} as { [token: string]: EmbedReportState.QueryRunStatus };

    datasets.map((dataset) => {
      // Note - Today, `dataset` represents datasetResults which always reference both a query run
      // and a run result that exist. IF anything changes upstream with how the API returns data,
      // this assumption may no longer be true, and error/empty cases may need to be handled here
      // similarly to the legacy flow.
      const queryRunning = [DataViewTypes.QueryRunStatus.Pending, DataViewTypes.QueryRunStatus.Enqueued].includes(
        dataset.queryRunState
      );
      queryRunStatuses[dataset.queryToken] = {
        token: dataset.queryToken,
        runToken: dataset.queryRunToken,
        state: dataset.queryRunState,
        dbtMetricToken: dataset.dbtMetricToken,
        isMetric: dataset.isMetric,
        running: queryRunning,
        datasetLoaded: !queryRunning,
      };
    });

    return queryRunStatuses;
  });

  export const reportRunGf = createSelector(queryRunStatus, (queryRun) => ({
    token: queryRun.token,
    running: queryRun.running,
  }));

  export const reportFilterControls = createSelector(
    fromReportFilterSelectors.selectFilterDefinitionsArray,
    fromReportFilterSelectors.selectFilterCurrentSelections,
    fromReportFilterSelectors.selectFilterSelections,
    fromReportFilterSelectors.selectFilterDefaults,
    (filters, currentSelections, selections, defaults) =>
      filters.map((filter) => {
        const t = filter.token;
        return recordToJSObject<ReportTypes.ReportFilterControl>(
          ReportFilterControlHelpers.convertToFilterControl(filter, currentSelections[t] ?? selections[t], defaults[t])
        );
      })
  );

  export const reportFilterOptions = createSelector(fromReportFilterSelectors.selectFilterOptions, (options) => {
    return Object.keys(options).reduce(
      (data, token) => {
        let opts = options[token];

        // For near-future optimization
        // const search = searches[token];

        // if (opts != null && opts.filterType !== ReportTypes.ReportFilterType.Range) {
        //   if (search) { // can be an empty string, which means don't search
        //     const regex = new RegExp(search, 'i');
        //     opts = {
        //       ...opts,
        //       values: opts.values.filter(o => regex.test(o.label)),
        //     };
        //   }
        //   if (opts.values.length > 1000) {
        //     opts = {
        //       ...opts,
        //       values: opts.values.slice(0, 1000),
        //     };
        //   }

        //   opts = {
        //     ...opts,
        //     values: opts.values.map(v => v)
        //   }
        // }

        data[token] = opts;
        return data;
      },
      {} as { [token: string]: ReportTypes.ReportFilterOptions }
    );
  });

  export const reportFilterTotalOptionCounts = createSelector(
    fromReportFilterSelectors.selectFilterOptions,
    (options) => {
      return Object.keys(options).reduce(
        (data, token) => {
          let opts = options[token];

          if (opts != null && opts.filterType !== ReportTypes.ReportFilterType.Range) {
            data[token] = opts.values.length;
          }

          return data;
        },
        {} as { [token: string]: number }
      );
    }
  );

  export const reportFilterSelectedOptionCounts = createSelector(
    fromReportFilterSelectors.selectFilterSelections,
    (options) => {
      return Object.keys(options).reduce(
        (data, token) => {
          let opts = options[token];

          if (opts != null && 'values' in opts) {
            data[token] = opts.values.length;
          }

          return data;
        },
        {} as { [token: string]: number }
      );
    }
  );

  export const reportFilterOptionsAll = createSelector(
    fromReportFilterSelectors.selectFilterOptions,
    fromReportFilterSelectors.selectFilterSelections,
    (options, selected) => {
      return Object.keys(options).reduce(
        (data, token) => {
          let opts = options[token];

          if (opts != null && opts.filterType !== ReportTypes.ReportFilterType.Range) {
            const selections = selected[token];
            if ('values' in selections) {
              // the union type has no discriminator field
              data[token] = selections.values.length === opts.values.length;
            }
          }

          return data;
        },
        {} as { [token: string]: boolean }
      );
    }
  );

  export const filters = createStructuredSelector({
    controls: reportFilterControls,
    options: reportFilterOptions,
    totalOptionCount: reportFilterTotalOptionCounts,
    selectedOptionCount: reportFilterSelectedOptionCounts,
    all: reportFilterOptionsAll,
  });

  export const reportContent = createSelector(reportInfo, charts, tables, (report, charts, tables) => ({
    report,
    charts,
    tables,
  }));

  // GF version
  export const reportContentGf = createSelector(reportInfoGf, charts, tables, (report, charts, tables) => ({
    report,
    charts,
    tables,
  }));

  export const uiState = createSelector(
    (state: AppState) => state.transient.editor.isFilterPanelOpen,
    (state: AppState) => state.transient.report.captureReportRunCreatedAt,
    (filterOpen, captureReportRunCreatedAt) => ({
      filterPanelOpen: filterOpen,
      captureReportRunCreatedAt,
    })
  );

  const inViewport = createSelector(virtualScrollSelectors.selectInViewport, (inViewport) => inViewport);

  const colorMaps = createSelector(fromReportViewerSelectors.selectReportViewerState, (report) => report.colorMappings);

  const downloadStatus = createSelector(
    fileExportSelectors.selectExportStatusByVizToken,
    (downloadStatus) => downloadStatus
  );

  // Embed state - takes into account the GF flag.
  export const embedState: Selector<AppState, EmbedReportState.SerializedReportState> = createStructuredSelector({
    report: createSelector(reportContentGf, (content) => content.report),
    charts: createSelector(reportContentGf, (content) => content.charts),
    tables: createSelector(reportContentGf, (content) => content.tables),
    account,
    reportRun,
    legacyEmbedDatasets,
    datasetResults: fromDatasetResultSelectors.getDatasetResults, // used only in GF context
    localQueryTokensSortedByCreationDate: getInitialFlagValue(FeatureFlag.CustomJsDatasetSupport)
      ? localQueryTokensSortedByQueryCreationDate
      : localQueryTokensSortedByCreationDateFilterdByAvailableDatasets,
    notebookVisuals: notebookViz,
    queryRunStatus: queryRunStatus,
    filters,
    ui: uiState,
    reportLevelColorMaps: colorMaps,
    features: FeatureSelectors.getEnabledFeatures,
    flamingo: FlamingoEmbedStateSelectors.flamingo,
    inViewport,
    downloadStatus,
  });

  export const embedBuilderState: Selector<AppState, EmbedReportState.SerializedReportState> = createStructuredSelector(
    {
      // TODO: should use reportContentGf, but the ReportViewer state isn't hydrated by this flow yet
      report: createSelector(reportContent, (content) => content.report),
      charts: createSelector(reportContent, (content) => content.charts),
      tables: createSelector(reportContent, (content) => content.tables),
      account,
      reportRun,
      legacyEmbedDatasets,
      datasetResults: fromDatasetResultSelectors.getDatasetResults,
      localQueryTokensSortedByCreationDate: getInitialFlagValue(FeatureFlag.CustomJsDatasetSupport)
        ? localQueryTokensSortedByQueryCreationDate
        : localQueryTokensSortedByCreationDateFilterdByAvailableDatasets,
      notebookVisuals: notebookViz,
      queryRunStatus: queryRunStatus,
      filters,
      ui: uiState,
      reportLevelColorMaps, // TODO: should use colorMapsGf, but not currently loaded
      features: FeatureSelectors.getEnabledFeatures,
      flamingo: FlamingoEmbedStateSelectors.flamingoForBuilder,
      inViewport,
      downloadStatus,
    }
  );
}
